import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const DAYS   = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

function parseYMD(str) {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatYMD(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDisplay(str) {
  if (!str) return null;
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

function buildGrid(year, month) {
  const first = new Date(year, month, 1).getDay();
  const days  = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < first; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function RhDatePicker({ value, onChange, placeholder = 'Pick date' }) {
  const selected = parseYMD(value);
  const today    = new Date();
  today.setHours(0, 0, 0, 0);

  const initial = selected || today;
  const [view, setView]         = useState({ year: initial.getFullYear(), month: initial.getMonth() });
  const [open, setOpen]         = useState(false);
  const [panelStyle, setPanelStyle] = useState({});
  const triggerRef = useRef(null);
  const panelRef   = useRef(null);

  // Sync view when value changes externally
  useEffect(() => {
    if (selected) setView({ year: selected.getFullYear(), month: selected.getMonth() });
  }, [value]); // eslint-disable-line

  // Close on outside click or scroll
  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        panelRef.current   && !panelRef.current.contains(e.target)
      ) setOpen(false);
    }
    function onScroll() { setOpen(false); }
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  function openCalendar() {
    const rect = triggerRef.current.getBoundingClientRect();
    const panelWidth  = 280;
    const panelHeight = 320;
    const spaceBelow  = window.innerHeight - rect.bottom;
    const openUpward  = spaceBelow < panelHeight && rect.top > panelHeight;
    const wouldOverflowRight = rect.left + panelWidth > window.innerWidth - 12;
    setPanelStyle({
      position: 'fixed',
      zIndex: 9999,
      width: panelWidth,
      left: wouldOverflowRight ? rect.right - panelWidth : rect.left,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 6 }
        : { top: rect.bottom + 6 }),
    });
    setOpen(true);
  }

  function prevMonth() {
    setView(v => {
      const m = v.month === 0 ? 11 : v.month - 1;
      const y = v.month === 0 ? v.year - 1 : v.year;
      return { year: y, month: m };
    });
  }
  function nextMonth() {
    setView(v => {
      const m = v.month === 11 ? 0 : v.month + 1;
      const y = v.month === 11 ? v.year + 1 : v.year;
      return { year: y, month: m };
    });
  }

  function pickDay(day) {
    if (!day) return;
    const picked = new Date(view.year, view.month, day);
    onChange(formatYMD(picked));
    setOpen(false);
  }

  function goToday() {
    onChange(formatYMD(today));
    setView({ year: today.getFullYear(), month: today.getMonth() });
    setOpen(false);
  }

  function clear() {
    onChange('');
    setOpen(false);
  }

  const grid = buildGrid(view.year, view.month);

  return (
    <div ref={triggerRef} style={{ position: 'relative', display: 'inline-block' }}>
      {/* Trigger */}
      <button
        onClick={() => (open ? setOpen(false) : openCalendar())}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          border: `1px solid ${open ? 'var(--rh-orange)' : 'var(--rh-border)'}`,
          borderRadius: 8, padding: '5px 11px', fontSize: 13, fontWeight: 500,
          background: '#fff', cursor: 'pointer',
          color: value ? 'var(--rh-text)' : 'var(--rh-text-faint)',
          boxShadow: open ? '0 0 0 3px rgba(255,69,0,0.12)' : 'none',
          transition: 'border-color 0.15s, box-shadow 0.15s',
          whiteSpace: 'nowrap',
        }}
      >
        {/* Calendar icon */}
        <svg width="13" height="13" fill="none" stroke={value ? 'var(--rh-orange)' : 'var(--rh-text-faint)'} strokeWidth="2" viewBox="0 0 24 24">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
        {value ? formatDisplay(value) : placeholder}
        {value && (
          <span
            onClick={(e) => { e.stopPropagation(); onChange(''); }}
            title="Clear"
            style={{
              marginLeft: 2, fontSize: 12, lineHeight: 1,
              color: 'var(--rh-text-faint)', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center',
            }}
          >
            <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </span>
        )}
      </button>

      {/* Calendar panel — rendered via portal so it escapes any overflow/z-index parent */}
      {open && createPortal(
        <div ref={panelRef} style={{
          ...panelStyle,
          background: '#fff', border: '1px solid var(--rh-border)',
          borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
          padding: '14px 14px 10px', userSelect: 'none',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <button onClick={prevMonth} style={navBtnStyle}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </button>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--rh-text)' }}>
              {MONTHS[view.month]} {view.year}
            </span>
            <button onClick={nextMonth} style={navBtnStyle}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>

          {/* Day headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 4 }}>
            {DAYS.map(d => (
              <div key={d} style={{
                textAlign: 'center', fontSize: 11, fontWeight: 700,
                color: 'var(--rh-text-faint)', padding: '3px 0',
                textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>
                {d}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
            {grid.map((day, i) => {
              if (!day) return <div key={i} />;
              const cellDate = new Date(view.year, view.month, day);
              cellDate.setHours(0, 0, 0, 0);
              const isToday    = cellDate.getTime() === today.getTime();
              const isSelected = selected && cellDate.getTime() === selected.getTime();
              return (
                <button
                  key={i}
                  onClick={() => pickDay(day)}
                  style={{
                    ...dayCellStyle,
                    background: isSelected
                      ? 'var(--rh-orange)'
                      : isToday
                      ? 'rgba(255,69,0,0.10)'
                      : 'transparent',
                    color: isSelected
                      ? '#fff'
                      : isToday
                      ? 'var(--rh-orange)'
                      : 'var(--rh-text)',
                    fontWeight: isSelected || isToday ? 700 : 400,
                    borderRadius: 7,
                    border: isToday && !isSelected ? '1.5px solid var(--rh-orange)' : '1.5px solid transparent',
                  }}
                  onMouseEnter={e => {
                    if (!isSelected) e.currentTarget.style.background = isToday ? 'rgba(255,69,0,0.18)' : 'rgba(0,0,0,0.05)';
                  }}
                  onMouseLeave={e => {
                    if (!isSelected) e.currentTarget.style.background = isToday ? 'rgba(255,69,0,0.10)' : 'transparent';
                  }}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            marginTop: 10, paddingTop: 10,
            borderTop: '1px solid var(--rh-border)',
          }}>
            <button onClick={clear} style={footerBtnStyle('#9ca3af')}>Clear</button>
            <button onClick={goToday} style={footerBtnStyle('var(--rh-orange)')}>Today</button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

const navBtnStyle = {
  background: 'none', border: '1px solid var(--rh-border)', borderRadius: 7,
  width: 28, height: 28, cursor: 'pointer', display: 'inline-flex',
  alignItems: 'center', justifyContent: 'center', color: 'var(--rh-text)',
  padding: 0, transition: 'background 0.1s',
};

const dayCellStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  height: 34, fontSize: 13, cursor: 'pointer', border: 'none',
  padding: 0, transition: 'background 0.1s',
};

function footerBtnStyle(color) {
  return {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 13, fontWeight: 600, color, padding: '2px 6px', borderRadius: 6,
  };
}
